const API_BASE = import.meta.env.VITE_API_URL || '/api';

// CSRF token cache
let csrfToken: string | null = null;

// 401 Unauthorized handler callback
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

// Validation issue structure from API
export interface ValidationIssue {
  path: string;
  message: string;
  code: string;
  received?: unknown;
}

// Extended API error response with validation issues
export interface ApiErrorResponse {
  statusCode: number;
  message: string;
  error?: string;
  // Error object from API wrapper
  errorDetails?: {
    code: string;
    message: string;
    issues: ValidationIssue[];
  };
}

// Custom error class for API errors
export class ApiClientError extends Error {
  public validationIssues: ValidationIssue[];

  constructor(
    public statusCode: number,
    message: string,
    public originalError?: ApiErrorResponse
  ) {
    super(message);
    this.name = 'ApiClientError';
    // Extract validation issues if present
    this.validationIssues = originalError?.errorDetails?.issues || [];
  }

  /**
   * Check if this is a validation error with field-level issues
   */
  hasValidationIssues(): boolean {
    return this.validationIssues.length > 0;
  }

  /**
   * Get validation issues for a specific field path
   */
  getIssuesForField(fieldPath: string): ValidationIssue[] {
    return this.validationIssues.filter((issue) => issue.path === fieldPath);
  }
}

// Fetch CSRF token from the API
async function fetchCsrfToken(): Promise<string> {
  const response = await fetch(`${API_BASE}/auth/csrf-token`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new ApiClientError(response.status, 'Failed to fetch CSRF token');
  }

  const data = await response.json();
  // Handle both wrapped ({ result: { csrfToken } }) and unwrapped ({ csrfToken }) responses
  const tokenData = data.result || data;
  csrfToken = tokenData.csrfToken;
  return csrfToken as string;
}

// Get current CSRF token (fetch if not cached)
async function getCsrfToken(): Promise<string> {
  if (!csrfToken) {
    return fetchCsrfToken();
  }
  return csrfToken;
}

// Clear CSRF token (call on logout)
export function clearCsrfToken(): void {
  csrfToken = null;
}

// Generic request handler
async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  retryOnCsrfError = true
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const config: RequestInit = {
    ...options,
    credentials: 'include', // Always include cookies
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // Add CSRF token for mutating requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method || '')) {
    const token = await getCsrfToken();
    config.headers = {
      ...config.headers,
      'x-csrf-token': token,
    };
  }

  const response = await fetch(url, config);

  // Handle response
  if (!response.ok) {
    let errorData: ApiErrorResponse;
    try {
      const jsonResponse = await response.json();
      // API wrapper format: { result, status, message, error: { code, message, issues } }
      errorData = {
        statusCode: jsonResponse.status || response.status,
        message: jsonResponse.message || response.statusText || 'Request failed',
        errorDetails: jsonResponse.error || undefined,
      };
    } catch {
      errorData = {
        statusCode: response.status,
        message: response.statusText || 'Request failed',
      };
    }

    // Retry once on CSRF error with a fresh token
    const isCsrfError =
      response.status === 400 &&
      (errorData.message?.toLowerCase().includes('csrf') ||
        errorData.message?.toLowerCase().includes('token'));

    if (isCsrfError && retryOnCsrfError) {
      csrfToken = null; // Clear stale token
      return request<T>(endpoint, options, false); // Retry without further retries
    }

    // Clear CSRF token on 403 (might be stale)
    if (response.status === 403) {
      csrfToken = null;
    }

    // Handle 401 Unauthorized - trigger global handler
    if (response.status === 401) {
      csrfToken = null;
      onUnauthorized?.();
    }

    throw new ApiClientError(response.status, errorData.message, errorData);
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text) as T;
}

// Exported API methods
export const apiClient = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),

  put: <T>(endpoint: string, data?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data ?? {}),
    }),

  delete: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: 'DELETE', body: JSON.stringify({}) }),

  // Utility to clear state
  clearCsrfToken,
};
